<script setup lang="ts">
    import { Download, Grid3x3, FileText, FileType, Copy, Printer } from 'lucide-vue-next';
    import { Button } from '@/components/ui/button';
    import { Table } from '@tanstack/vue-table';
    import { usePage } from '@inertiajs/vue3';
    import { exportTableAsPdf } from '@/utils/pdfExport';
    import type { SharedData } from '@/types';
    import {
        DropdownMenu,
        DropdownMenuContent,
        DropdownMenuItem,
        DropdownMenuTrigger,
    } from '@/components/ui/dropdown-menu';
    import { ref, computed } from 'vue';
    import { useI18n } from 'vue-i18n';
    import axios from 'axios';
    import { useDateFormat } from '@/composables/useDateFormat';

    const page = usePage<SharedData>();
    const { t } = useI18n();
    const { formatDMYFromIso } = useDateFormat();

    const availableExports = computed(() => {
        const currentPath = currentPathname.value;

        let exports: ('excel' | 'csv' | 'pdf' | 'copy' | 'print')[] = [
            'excel',
            'csv',
            'pdf',
            'copy',
            'print',
        ];

        // On payrolls index view, only allow PDF export via the payslips log endpoint
        if (currentPath === '/salary/payrolls' && hasPayslipsLogPermission?.value) {
            return ['pdf'];
        }

        if (currentPath.includes('corpay-one') || currentPath.includes('power-bi')) {
            return ['excel', 'csv'];
        }

        if (props.table?.getFilteredRowModel().rows.length === 0) {
            return ['copy'];
        }

        const user = page.props.auth.user;
        if (user.is_client && !user.selected_company_id) {
            exports = exports.filter((exp) => exp !== 'pdf');
        }

        return exports;
    });

    interface Props {
        table?: Table<any>;
        exportEndpoint?: string;
        filename?: string;
    }

    const props = withDefaults(defineProps<Props>(), {
        exportEndpoint: '/dashboard/admin/v3/export',
        filename: 'export',
    });

    const isExporting = ref(false);

    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const currentPathname = computed(() => {
        const url = (page as any)?.url || window.location.pathname;
        const pathname = new URL(url, window.location.origin).pathname;

        return pathname.replace(/\/+$/, '');
    });

    const isPayrollsIndexPage = computed(() => currentPathname.value === '/salary/payrolls');

    const hasPayslipsLogPermission = computed(() => {
        const perms = page.props.auth.user?.permissions ?? [];

        return Array.isArray(perms) && perms.includes('payslips-log_payslips-log');
    });

    const pdfLabel = computed(() => {
        if (isPayrollsIndexPage.value && hasPayslipsLogPermission.value) {
            // Primary key requested by user, fallback to existing key if missing
            const primary = t('payslips_log.entries.pageheader');
            
            return primary === 'payslips_log.entries.pageheader'
                ? t('payslips_log.entries.page_header')
                : primary;
        }

        return 'PDF';
    });

    const getDropdownLabel = (columnId: string, rawValue: any): string => {
        let dataFieldGroup = '';

        if (columnId.includes('bookkeeping')) {
            dataFieldGroup = 'bookkeeping_status';
        } else if (columnId.includes('report')) {
            dataFieldGroup = 'report_status';
        } else if (columnId.includes('vat')) {
            dataFieldGroup = 'vat_status';
        } else if (columnId.includes('reload')) {
            const reloadMap: Record<number, string> = { 0: 'No', 1: 'Yes', 2: 'Loading' };
            return reloadMap[rawValue as keyof typeof reloadMap] || String(rawValue);
        }

        const statusOptions = (page.props as any).statusOptions;
        if (statusOptions && statusOptions[dataFieldGroup]) {
            const option = statusOptions[dataFieldGroup].find((opt: any) => opt.value === rawValue);
            if (option) {
                return option.label;
            }
        }

        return String(rawValue) || '';
    };

    const isDropdownColumn = (columnId: string, rawValue: any): boolean => {
        return (
            (typeof rawValue === 'number' &&
                (columnId.includes('status') ||
                    columnId.includes('reload') ||
                    columnId.includes('frequency') ||
                    columnId.includes('report'))) ||
            ((rawValue === 0 ||
                rawValue === 1 ||
                rawValue === '0' ||
                rawValue === '1' ||
                rawValue === true ||
                rawValue === false) &&
                !columnId.includes('deadline') &&
                !columnId.includes('year'))
        );
    };

    const extractTableData = () => {
        if (!props.table) {
            return { headers: [], data: [] };
        }

        const visibleColumns = props.table.getAllColumns().filter((col) => {
            if (!col.getIsVisible()) return false;
            if (col.id.includes('select')) return false;
            if (col.id.includes('action')) return false;
            if (col.id.startsWith('_')) return false;
            if (col.id.includes('internal')) return false;
            return true;
        });

        const headers = visibleColumns.map((column) => {
            if (typeof column.columnDef.header === 'function') {
                try {
                    const headerResult = column.columnDef.header({
                        column,
                        header: column as any,
                        table: props.table as any,
                    });
                    if (headerResult?.props?.title) {
                        return t(headerResult.props.title);
                    }
                    return column.id;
                } catch (e) {
                    return column.id;
                }
            }

            if (typeof column.columnDef.header === 'string') {
                if (column.columnDef.header.includes('.')) {
                    try {
                        const translated = t(column.columnDef.header);
                        return translated !== column.columnDef.header
                            ? translated
                            : column.columnDef.header;
                    } catch {
                        return column.columnDef.header;
                    }
                }
                return column.columnDef.header;
            }

            return column.id;
        });

        const allRows = props.table.getExpandedRowModel().rows;
        const data: string[][] = [];

        const processRow = (row: any) => {
            if (row.getIsGrouped?.() || row.groupingValue !== undefined) {
                const groupRow = new Array(headers.length).fill('');
                groupRow[0] = row.groupingValue || row.id || 'Group Header';
                data.push(groupRow);
            } else {
                // Process regular data rows
                const rowData = visibleColumns.map((column) => {
                    const rawValue = row.getValue(column.id);
                    const columnId = column.id;

                    const isDateColumn = (id: string) => {
                        // Match exact ids and nested paths that end with these keys
                        return (
                            id === 'employment_date' ||
                            id.endsWith('.employment_date') ||
                            id === 'report_deadline' ||
                            id.endsWith('.report_deadline') ||
                            id === 'bookkeeping_deadline' ||
                            id.endsWith('.bookkeeping_deadline') ||
                            id === 'vat_deadline' ||
                            id.endsWith('.vat_deadline') ||
                            id === 'accouting_year' ||
                            id.endsWith('.accouting_year')
                        );
                    };

                    // Handle status indicator columns (not dropdowns)
                    if (
                        columnId === 'status' ||
                        (columnId.endsWith('_status') &&
                            !columnId.includes('bookkeeping') &&
                            !columnId.includes('report') &&
                            !columnId.includes('vat') &&
                            !columnId.includes('reload'))
                    ) {
                        const statusMap: Record<number, string> = {
                            0: t('users.grouping.deactivated'),
                            1: t('users.grouping.active'),
                        };
                        return statusMap[rawValue as keyof typeof statusMap] || String(rawValue);
                    }

                    // Handle checkbox columns in payroll tables (0/1 to Yes/No)
                    if (
                        (rawValue === 0 || rawValue === 1) &&
                        (columnId.includes('reported') ||
                            columnId.includes('approval') ||
                            columnId.includes('approved') ||
                            columnId.includes('sent') ||
                            columnId.includes('finalized') ||
                            columnId.includes('payroll') ||
                            columnId.includes('payslip') ||
                            columnId.includes('days_vacation') ||
                            columnId.includes('flex_savings') ||
                            columnId.includes('production_unit') ||
                            columnId.includes('danmarks_statistik') ||
                            columnId.includes('health_insurance') ||
                            columnId.includes('has_free_car') ||
                            columnId.includes('has_free_phone') ||
                            columnId.includes('has_lunch_arrangement') ||
                            columnId.includes('has_pension') ||
                            columnId.includes('kilometer') ||
                            columnId.includes('diaeter') ||
                            columnId.includes('has_fixed_add_ons'))
                    ) {
                        const checkboxMap: Record<number, string> = {
                            0: t('forms.no'),
                            1: t('forms.yes'),
                        };
                        return (
                            checkboxMap[rawValue as keyof typeof checkboxMap] || String(rawValue)
                        );
                    }

                    // Handle dropdown columns
                    if (isDropdownColumn(columnId, rawValue)) {
                        return getDropdownLabel(columnId, rawValue);
                    }

                    // Format known date columns to dd.mm.YYYY
                    if (isDateColumn(columnId)) {
                        return rawValue ? formatDMYFromIso(String(rawValue)) : '';
                    }

                    // Handle model data (User, Company, etc.)
                    if (Array.isArray(rawValue) && rawValue[0]?.name) {
                        return rawValue
                            .map((item) => item.name || item.display_name || String(item))
                            .join(', ');
                    }

                    if (rawValue && typeof rawValue === 'object' && rawValue.name) {
                        return rawValue.name || rawValue.display_name || String(rawValue);
                    }

                    return String(rawValue || '');
                });

                data.push(rowData);
            }

            if (row.subRows?.length > 0) {
                row.subRows.forEach(processRow);
            }
        };

        allRows.forEach(processRow);

        return { headers, data };
    };

    const getCsrfToken = (): string => {
        const metaToken = document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute('content');
        if (metaToken) return metaToken;

        const formToken = document.querySelector('input[name="_token"]')?.getAttribute('value');
        if (formToken) return formToken;

        console.error('CSRF token not found');
        return '';
    };

    const exportData = async (type: 'excel' | 'csv' | 'pdf' | 'copy' | 'print') => {
        if (isExporting.value) {
            return;
        }

        if (type === 'copy') {
            handleCopyToClipboard();
            return;
        }

        if (type === 'print') {
            handlePrint();
            return;
        }

        if (type === 'pdf') {
            handlePdfExport();
            return;
        }

        try {
            isExporting.value = true;
            const { headers, data } = extractTableData();

            if (headers.length === 0 || data.length === 0) {
                console.warn('No data found to export');
                return;
            }

            const formData = new FormData();
            formData.append('tableHeaders', JSON.stringify(headers));
            formData.append('tableData', JSON.stringify(data));
            formData.append('_token', getCsrfToken());

            const response = await axios.post(`${props.exportEndpoint}/${type}`, formData, {
                responseType: 'blob',
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            const contentDisposition = response.headers['content-disposition'];
            let filename = `${props.filename}_${new Date().toISOString().split('T')[0]}.${type === 'excel' ? 'xlsx' : type}`;

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed:', error);
        } finally {
            isExporting.value = false;
        }
    };

    const handlePdfExport = async () => {
        const currentPath = currentPathname.value;

        // Override PDF export on payrolls index page to use server-generated payslips log PDF
        if (currentPath === '/salary/payrolls' && hasPayslipsLogPermission.value) {
            const url = route('v2.salary.payslips-log.download', { company_id: selectedCompanyId.value });
            window.open(url, '_blank');
            return;
        }

        // Default client-side PDF export for other pages
        const { headers, data } = extractTableData();
        if (headers.length === 0 || data.length === 0) {
            console.warn('No data found to export');
            return;
        }
        try {
            await exportTableAsPdf({ headers, data }, props.filename, {
                orientation: 'landscape',
                pageSize: 'LEGAL',
                headerColor: '#4472C4',
                headerTextColor: 'white',
                alternatingRowColor: '#f2f2f2',
            });
        } catch (error) {
            console.error('PDF export failed:', error);
        }
    };

    const handleCopyToClipboard = () => {
        const { headers, data } = extractTableData();

        if (headers.length === 0 || data.length === 0) {
            console.warn('No data found to copy');
            return;
        }

        const headerRow = headers.join('\t');
        const dataRows = data.map((row) => row.join('\t'));
        const textToCopy = [headerRow, ...dataRows].join('\n');

        navigator.clipboard
            .writeText(textToCopy)
            .then(() => {})
            .catch((err) => {
                console.error('Failed to copy to clipboard:', err);
            });
    };

    const handlePrint = () => {
        const { headers, data } = extractTableData();

        if (headers.length === 0 || data.length === 0) {
            console.warn('No data found to print');
            return;
        }

        const tableHTML = `
            <table border="1" style="border-collapse: collapse; width: 100%;">
                <thead>
                    <tr>${headers.map((h) => `<th style="padding: 8px;">${h}</th>`).join('')}</tr>
                </thead>
                <tbody>
                    ${data
                        .map(
                            (row) =>
                                `<tr>${row.map((cell) => `<td style="padding: 8px;">${cell}</td>`).join('')}</tr>`,
                        )
                        .join('')}
                </tbody>
            </table>
        `;

        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.write(`
                <html>
                    <head><title>Print Table</title></head>
                    <body>${tableHTML}</body>
                </html>
            `);
            printWindow.document.close();
            printWindow.print();
            printWindow.close();
        }
    };
</script>

<template>
    <DropdownMenu>
        <DropdownMenuTrigger as-child>
            <Button
                variant="ghost"
                size="sm"
                class="ml-auto"
                :disabled="isExporting"
            >
                <Download />
                <span class="sr-only">Export options</span>
            </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
            <DropdownMenuItem
                v-if="availableExports.includes('excel')"
                @click="exportData('excel')"
                :disabled="isExporting"
                class="cursor-pointer"
            >
                <Grid3x3 />
                Excel
            </DropdownMenuItem>
            <DropdownMenuItem
                v-if="availableExports.includes('csv')"
                @click="exportData('csv')"
                :disabled="isExporting"
                class="cursor-pointer"
            >
                <FileText class="text-muted-foreground" />
                CSV
            </DropdownMenuItem>
            <DropdownMenuItem
                v-if="availableExports.includes('pdf')"
                @click="exportData('pdf')"
                :disabled="isExporting"
                class="cursor-pointer"
            >
                <FileType />
                {{ pdfLabel }}
            </DropdownMenuItem>
            <DropdownMenuItem
                v-if="availableExports.includes('copy')"
                @click="exportData('copy')"
                class="cursor-pointer"
            >
                <Copy />
                {{ t('tables.export.copy') }}
            </DropdownMenuItem>
            <DropdownMenuItem
                v-if="availableExports.includes('print')"
                @click="exportData('print')"
                class="cursor-pointer"
            >
                <Printer />
                Print
            </DropdownMenuItem>
        </DropdownMenuContent>
    </DropdownMenu>
</template>
